import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useLocation, useNavigate } from 'react-router-dom';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import { toast } from 'react-toastify';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import { getError } from '../utils';
import Card from 'react-bootstrap/Card';
import { Container, Form } from 'react-bootstrap';
import Pagination from '../components/general/Pagination';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' }; // a kept error would outlive the next successful fetch (the render checks it after loading)
    case 'FETCH_SUCCESS':
      return {
        ...state,
        legends: action.payload.legends,
        page: action.payload.page,
        pages: action.payload.pages,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };

    case 'DELETE_REQUEST':
      return { ...state, loadingDelete: true, successDelete: false };
    case 'DELETE_SUCCESS':
      return {
        ...state,
        loadingDelete: false,
        successDelete: true,
      };
    case 'DELETE_FAIL':
      return { ...state, loadingDelete: false, successDelete: false };
    case 'DELETE_RESET':
      return { ...state, loadingDelete: false, successDelete: false };

    default:
      return state;
  }
};

export default function LegendsListScreen() {
  const [
    {
      loading,
      error,
      legends,
      pages,
      loadingCreate,
      loadingDelete,
      successDelete,
    },
    dispatch,
  ] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const navigate = useNavigate();
  const { search } = useLocation(); // gets the query parameters of the URL and saves them in the variable called 'search'
  const sp = new URLSearchParams(search); // creates a new instance of the URLSearchParams class, passing in the search string, wich allows to apply several methods to work with the query parameters
  const page = sp.get('page') || 1; // Retrieves the value of the 'page' query parameter (the number on wich page the user is on)
  const searchQuery = sp.get('search') || ''; // Retrieves the 'search' parameter from the URL query string; defaults to an empty string if not present

  const [searchInput, setSearchInput] = useState(searchQuery); // Local state for search input

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });
      try {
        const { data } = await api.get(
          `/api/legends/admin?page=${page}&search=${encodeURIComponent(
            searchQuery
          )}`
        );

        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    }; // fetches the data of all legends and information that is being used for the pagination from the backend

    if (successDelete) {
      dispatch({ type: 'DELETE_RESET' });
    } else {
      fetchData();
    }
  }, [page, successDelete, searchQuery]);

  const createHandler = async () => {
    if (window.confirm('Willst du wirklich neue Legends erstellen?')) {
      // if the user confirms that he wants to create new legends, the following code will run
      try {
        dispatch({ type: 'CREATE_REQUEST' });
        const { data } = await api.post('/api/legends', {}); // posts new legends to the '/api/legends' backend api
        toast.success('Legends erstellt');
        dispatch({ type: 'CREATE_SUCCESS' });
        navigate(`/admin/legend-set/${data.legendSet._id}`); // navigates the user to the newly created legend set
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'CREATE_FAIL',
        });
      }
    }
  };

  const deleteHandler = async (legendSet) => {
    if (window.confirm('Willst du diese Legends wirklich löschen?')) {
      // if the user confirms that he wants to delete the legends, the following code will run
      try {
        await api.delete(`/api/legends/${legendSet._id}`); // sends a delete request for the selected legend set to the backend
        toast.success('Legends gelöscht');
        dispatch({ type: 'DELETE_SUCCESS' });
      } catch (err) {
        toast.error(getError(err));
        dispatch({
          type: 'DELETE_FAIL',
        });
      }
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    const sanitizedInput = searchInput.replace(/\s+/g, ''); // Remove spaces
    navigate(
      sanitizedInput
        ? `/admin/legends?page=1&search=${encodeURIComponent(sanitizedInput)}`
        : `/admin/legends?page=1`
    ); // Navigates to the UserListScreen with the search query and resets to page 1 if sanitizedInput exists; otherwise, navigates to page 1 without any search filter
  };

  return (
    <Container>
      <div>
        <title>Legends</title>

        <h1 className="screen-heading">Legends</h1>

        {/* Search Form */}
        <Form onSubmit={submitHandler} className="d-flex my-5">
          <Form.Control
            type="text"
            placeholder="Search by name or _id"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label="Search by name or _id"
          />
          <Button type="submit" variant="secondary" className="mx-3">
            Search
          </Button>
        </Form>

        {loading || loadingCreate || loadingDelete ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <>
            <Row>
              <Col>
                <div className="d-flex justify-content-center mb-5">
                  <Button
                    variant="secondary"
                    type="button"
                    onClick={createHandler}
                  >
                    Legends erstellen
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="d-xl-none">
              {legends.map((set) => (
                <Col key={set._id} xs={12} sm={12} md={12} lg={6}>
                  <Card className="products-card">
                    <Card.Body className="text-center">
                      <Card.Title className="code mb-4">{set.name}</Card.Title>
                      <div className="divider"></div>
                      <div className="code-info">
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Supported Layouts:
                          </strong>{' '}
                          <span className="no-wrap">
                            {set.supportedLayouts.length > 0
                              ? set.supportedLayouts.join(', ')
                              : 'none'}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Active:
                          </strong>{' '}
                          <span className="no-wrap">
                            {set.isActive ? 'Yes' : 'No'}
                          </span>
                        </Card.Text>
                        <Card.Text>
                          <strong style={{ textDecoration: 'underline' }}>
                            Aktionen:
                          </strong>
                          <span className="no-wrap w-100 mt-1">
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() =>
                                navigate(`/admin/legend-set/${set._id}`)
                              }
                            >
                              Bearbeiten
                            </Button>
                            &nbsp;
                            <Button
                              variant="secondary"
                              type="button"
                              onClick={() => deleteHandler(set)}
                            >
                              Löschen
                            </Button>
                          </span>
                        </Card.Text>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <div className="d-none d-xl-block">
              <table className="table">
                <thead>
                  <tr>
                    <th>NAME</th>
                    <th>SUPPORTED LAYOUTS</th>
                    <th>ACTIVE</th>
                    <th>AKTIONEN</th>
                  </tr>
                </thead>
                <tbody>
                  {legends.map((set) => (
                    <tr key={set._id}>
                      <td>{set.name}</td>
                      <td>
                        {set.supportedLayouts.length > 0
                          ? set.supportedLayouts.join(', ')
                          : 'none'}
                      </td>
                      <td>{set.isActive ? 'Yes' : 'No'}</td>
                      <td>
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() =>
                            navigate(`/admin/legend-set/${set._id}`)
                          }
                        >
                          Bearbeiten
                        </Button>
                        &nbsp;
                        <Button
                          variant="secondary"
                          type="button"
                          onClick={() => deleteHandler(set)}
                        >
                          Löschen
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              pages={pages}
              page={Number(page)}
              baseUrl="/admin/legends"
              search={searchQuery}
            />
          </>
        )}
      </div>
    </Container>
  );
}
